import React, { useState, useEffect, useRef, useCallback } from 'react';
import { projectsAPI } from '../api';
import MarkdownContent from './MarkdownContent';

function ProjectFeed({ projectId, isOwner, username }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [newPostContent, setNewPostContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [message, setMessage] = useState('');
  const observerTarget = useRef(null);
  const limit = 10;

  const loadPosts = useCallback(async (currentOffset = 0, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await projectsAPI.getFeedPosts(projectId, limit, currentOffset);
      if (response.data.success) {
        const newPosts = response.data.posts;
        if (append) {
          setPosts(prev => [...prev, ...newPosts]);
        } else {
          setPosts(newPosts);
        }
        setHasMore(response.data.has_more);
        setOffset(currentOffset + newPosts.length);
      }
    } catch (error) {
      console.error('Error loading feed:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [projectId, limit]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadPosts(offset, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, offset, loadPosts]);

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim()) return;

    setSubmitting(true);
    try {
      const response = await projectsAPI.addFeedPost(projectId, newPostContent);
      if (response.data.success) {
        setPosts([response.data.post, ...posts]);
        setNewPostContent('');
        setMessage('Post added successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error adding post:', error);
      setMessage(error.response?.data?.error || 'Failed to add post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPost = (post) => {
    setEditingPostId(post.post_id);
    setEditContent(post.content);
  };

  const handleUpdatePost = async (postId) => {
    if (!editContent.trim()) return;

    try {
      const response = await projectsAPI.updateFeedPost(projectId, postId, editContent);
      if (response.data.success) {
        setPosts(posts.map(p => p.post_id === postId ? response.data.post : p));
        setEditingPostId(null);
        setEditContent('');
        setMessage('Post updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating post:', error);
      setMessage(error.response?.data?.error || 'Failed to update post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;

    try {
      const response = await projectsAPI.deleteFeedPost(projectId, postId);
      if (response.data.success) {
        setPosts(posts.filter(p => p.post_id !== postId));
        setMessage('Post deleted successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      setMessage(error.response?.data?.error || 'Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="project-feed">
      {message && <div className="alert alert-info">{message}</div>}

      {isOwner && (
        <div className="feed-post-form">
          <h3>Post Update</h3>
          <form onSubmit={handleSubmitPost}>
            <textarea
              className="form-control"
              placeholder="Share an update with your followers..."
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              rows="4"
              maxLength="5000"
              disabled={submitting}
            />
            <div className="d-flex justify-between align-center mt-2">
              <span className="text-muted text-small">
                {newPostContent.length}/5000 characters
              </span>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={submitting || !newPostContent.trim()}
              >
                {submitting ? 'Posting...' : 'Post Update'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="feed-posts">
        {loading && posts.length === 0 ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading feed...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="empty-state">
            <h4>No updates yet</h4>
            <p className="text-muted">
              {isOwner ? 'Be the first to post an update!' : 'Check back later for updates'}
            </p>
          </div>
        ) : (
          <>
            {posts.map((post) => (
              <div key={post.post_id} className="feed-post">
                <div className="feed-post-header">
                  <div className="feed-post-author">
                    <strong>{post.author}</strong>
                    <span className="feed-post-date">{formatDate(post.created_at)}</span>
                    {post.created_at !== post.updated_at && (
                      <span className="feed-post-edited"> (edited)</span>
                    )}
                  </div>
                  {(isOwner || post.author === username) && (
                    <div className="feed-post-actions">
                      {post.author === username && (
                        <button
                          onClick={() => handleEditPost(post)}
                          className="btn btn-secondary btn-sm"
                        >
                          Edit
                        </button>
                      )}
                      {(post.author === username || isOwner) && (
                        <button
                          onClick={() => handleDeletePost(post.post_id)}
                          className="btn btn-danger btn-sm"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {editingPostId === post.post_id ? (
                  <div className="feed-post-edit">
                    <textarea
                      className="form-control"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows="4"
                      maxLength="5000"
                    />
                    <div className="d-flex gap-2 mt-2">
                      <button
                        onClick={() => handleUpdatePost(post.post_id)}
                        className="btn btn-primary btn-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingPostId(null);
                          setEditContent('');
                        }}
                        className="btn btn-secondary btn-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="feed-post-content">
                    <MarkdownContent content={post.content} />
                  </div>
                )}
              </div>
            ))}
            
            {hasMore && (
              <div ref={observerTarget} className="feed-loading-more">
                {loadingMore && (
                  <div className="loading-inline">
                    <div className="spinner-small"></div>
                    <span>Loading more...</span>
                  </div>
                )}
              </div>
            )}

            {!hasMore && posts.length > 0 && (
              <div className="feed-end">
                <p className="text-muted">You've reached the end</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProjectFeed;
